import { AlertTriangle, RefreshCcw } from "lucide-react";

interface ErrorBoundaryFallbackProps {
  error: Error;
  resetError: () => void;
}

// A stale open tab across a deploy reloads itself once to pick up the new
// build. Guard with sessionStorage so a genuinely broken deploy (chunk
// still 404s after reload) shows the normal error screen instead of
// silently reloading forever.
const CHUNK_RELOAD_FLAG = "anchorstay-chunk-reload-attempted";

export function ErrorBoundaryFallback({ error, resetError }: ErrorBoundaryFallbackProps) {
  const isChunkError =
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("Importing a module script failed");

  if (isChunkError && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
    // If it's a Vite chunk error (common in PWAs after a new deployment),
    // clear the service worker caches and force a reload.
    if ('caches' in window) {
      caches.keys().then((names) => {
        Promise.all(names.map(name => caches.delete(name))).then(() => {
          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4 text-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground animate-pulse">Updating app version...</p>
        </div>
      </div>
    );
  }

  const handleRefresh = () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        Promise.all(names.map(name => caches.delete(name))).then(() => {
          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
      <div className="h-16 w-16 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mb-4">
        <AlertTriangle className="h-8 w-8" />
      </div>
      <h2 className="text-2xl font-semibold mb-2 text-foreground">Something went wrong</h2>
      <p className="text-muted-foreground mb-6 max-w-md">
        We encountered an unexpected error. Please refresh the app to continue.
      </p>
      
      <div className="p-4 bg-muted/50 rounded-md w-full max-w-md mb-8 overflow-auto text-left border">
        <p className="font-mono text-xs text-muted-foreground break-all">
          {error?.message || "Unknown error occurred"}
        </p>
      </div>

      <button
        onClick={handleRefresh}
        className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium shadow-sm hover:bg-primary/90 transition-colors"
      >
        <RefreshCcw className="h-4 w-4" />
        Refresh App
      </button>
    </div>
  );
}
